import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import * as Sentry from '@sentry/vue';
import { VueFire, VueFireAuth } from 'vuefire';
import { app as firebaseApp } from './lib/firebase';
import App from './App.vue';
import router from './router';
import './assets/main.css';

// Handle chunk load errors (stale cached HTML referencing old JS chunks)
// This happens when a new deployment occurs and users have old HTML cached
const RELOAD_STORAGE_KEY = 'app-reload-attempted';

window.addEventListener('error', (event) => {
  // Check if it's a chunk load error
  const isChunkError =
    event.message?.includes('Failed to fetch dynamically imported module') ||
    event.message?.includes('Failed to load module script') ||
    event.message?.includes('Importing a module script failed');

  if (isChunkError) {
    // Check if we've already tried reloading recently (prevent infinite loops)
    const lastReload = sessionStorage.getItem(RELOAD_STORAGE_KEY);
    const now = Date.now();

    if (!lastReload || (now - parseInt(lastReload)) > 10000) {
      console.warn('Detected stale chunk, reloading page...');
      sessionStorage.setItem(RELOAD_STORAGE_KEY, now.toString());
      window.location.reload();
    } else {
      console.error('Chunk load error persists after reload:', event.message);
    }
  }
});

// Also handle chunk errors from dynamic imports in router
router.onError((error) => {
  const isChunkError = 
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Failed to load module script') ||
    /Loading chunk \d+ failed/.test(error.message);
  
  if (isChunkError) {
    const lastReload = sessionStorage.getItem(RELOAD_STORAGE_KEY);
    const now = Date.now();
    
    if (!lastReload || (now - parseInt(lastReload)) > 10000) {
      console.warn('Detected stale chunk in router, reloading page...');
      sessionStorage.setItem(RELOAD_STORAGE_KEY, now.toString());
      window.location.reload();
    }
  }
});

const app = createApp(App);
const head = createHead();

// Initialize Sentry (production only)
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        blockAllMedia: false,
        maskAllText: false,
        maskAllInputs: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Sample 10% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    environment: import.meta.env.MODE,
    ignoreErrors: [
      // Browser extension errors
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });
}

app.use(createPinia());
app.use(VueFire, {
  firebaseApp,
  modules: [VueFireAuth()],
});
app.use(router);
app.use(head);
app.mount('#app');
